import React, { useEffect, useState, useCallback, useRef } from "react";
import { useThree, ThreeEvent } from "@react-three/fiber";
import * as THREE from "three";
import { observer } from "mobx-react-lite";
import { useFloorplannerStore } from '../../store/floorplannerStore';
import { Room, RulerLineType, WallConnectionEnd, WallConnectionStart } from "../../types/floorplannerTypes";
import { editorStore } from "../../store/editorStore";
import { renderStore } from "../../store/renderStore";
import { updateDragHandles } from "./updateDragHandles";
import { projectToWorld } from "./projectToWorld";
import { action, set } from "mobx";
import { dragControlHandle } from "./dragControlHandle";
import { updateAlignmentLines } from "./updateAligmentLines";
import { dragWallHandle } from "./dragWallHandle";
import { handleSize, handleWidth } from "./createMiddleDragHandle";
import { dragStartHandle } from "./dragStartHandle";
import { dragEndHandle } from "./dragEndHandle";
import { updateWallConnections } from "./updateWallConnections";
import { dragMiddleVHandle } from "./dragMiddleVHandle";
import { dragMiddleHHandle } from "./dragMiddleHHandle";
import RulerLine from "./RulerLine";
import { useDragSelectedObjects } from "../../hooks/useDragSelectedObjects";
import { findSymbolFootprintSnapPoint } from "../../utils/symbolFootprintSnapping";
import { CANVAS_ACCENT_COLOR, CANVAS_HOVER_COLOR, canvasHandleFillColor } from "../../constants/brandColors";

const RulerLines = observer(() => {
  const floorplannerStore = useFloorplannerStore();
  const [rooms, setRooms] = useState<Room[]>([]); // Explicitly set the type to Room[]
  const { camera, gl } = useThree();
  const dragLine = useRef<RulerLineType | undefined>(undefined);
  const dragOffset = useRef<[number, number, number, number] | null>(null);
  const dragHandle = useRef<{ handle: string; startPosition: THREE.Vector3; endPosition: THREE.Vector3 }>({
    handle: "",
    startPosition: new THREE.Vector3(),
    endPosition: new THREE.Vector3(),
  });
  const [hoveredLine, setHoveredLine] = useState<string | null>(null);
  const [hoveredHandle, setHoveredHandle] = useState<string | null>(null);
  const isHovered = (handle: string) => hoveredHandle === handle;
  const [dragEndFreeToConnect, setDragEndFreeToConnect] = useState(false);
  const noopRaycast = () => null;
  const wallMinMidHandleSize = editorStore.zoomLevel < 3 ? Math.max(0.15, 0.15 / editorStore.zoomLevelDivisor()) : 0.1; // Minimum size of the mid-handle
  const wallMidHandleGap = 0.09 / editorStore.zoomLevelDivisor(); // Gap between the mid-handles
  const aboutToModify = useRef(false);
  const { startDraggingGroupSelection, isDraggingGroupSelection } = useDragSelectedObjects(gl, camera);
  const selectionHandlesVisible = editorStore.shouldShowSelectionHandles();

  const handlePointerMove = useCallback(
    action((e: PointerEvent) => {
      if (dragLine.current !== undefined && dragOffset.current) {
        if (aboutToModify.current) {
          aboutToModify.current = false;
          floorplannerStore.beginUndoTransaction('dragRuler');
          floorplannerStore.recordRulerForUndo(dragLine.current.id);
        }
        if (editorStore.rulerDragging !== dragLine.current.id) editorStore.setRulerDragging(dragLine.current.id);
        let [newX, newY] = projectToWorld(e.clientX, e.clientY, gl, camera);
        const [startOffsetX, startOffsetY, endOffsetX, endOffsetY] =
          dragOffset.current;
        const ruler = dragLine.current;
        if (!ruler) return;

        let delta = new THREE.Vector2(0, 0);
        let snapped = false;
        if (dragHandle.current.handle === "start") {
          snapped = dragStartHandle(ruler, startOffsetX, startOffsetY, newX, newY, dragEndFreeToConnect, delta);
        } else if (dragHandle.current.handle === "end") {
          snapped = dragEndHandle(ruler, endOffsetX, endOffsetY, newX, newY, dragEndFreeToConnect, delta);
        } else if (dragHandle.current.handle === "middleV") {
          snapped = dragMiddleVHandle(ruler, startOffsetX, startOffsetY, newX, newY, delta);
        } else if (dragHandle.current.handle === "middleH") {
          snapped = dragMiddleHHandle(ruler, startOffsetX, startOffsetY, newX, newY, delta);
        } else if (dragHandle.current.handle === "wall") {
          snapped = dragWallHandle(ruler, startOffsetX, startOffsetY, endOffsetX, endOffsetY, newX, newY, delta);
        } else if (dragHandle.current.handle === "control") {
          dragControlHandle(ruler, newX, newY);
        }
        let latestLine = floorplannerStore.findObjectId(ruler.id) as RulerLineType | undefined;
        if (latestLine) {
          // Land ruler endpoints exactly on symbol footprint corners so a
          // measurement chain across a piece of furniture matches the numbers
          // shown in the property panel.
          const endpoint =
            dragHandle.current.handle === "start"
              ? "start"
              : dragHandle.current.handle === "end"
                ? "end"
                : null;
          if (endpoint && !e.shiftKey && !editorStore.isAltPressed) {
            const symbolSnap = findSymbolFootprintSnapPoint(
              latestLine[endpoint],
              floorplannerStore.symbolsMap.values(),
              editorStore.interactionSnappingTolerance(),
            );
            if (symbolSnap && !symbolSnap.position.equals(latestLine[endpoint])) {
              const start = endpoint === "start" ? symbolSnap.position.clone() : latestLine.start.clone();
              const end = endpoint === "end" ? symbolSnap.position.clone() : latestLine.end.clone();
              floorplannerStore.updateObjectPosition(ruler.id, start, end);
              latestLine =
                (floorplannerStore.findObjectId(ruler.id) as RulerLineType | undefined) ?? latestLine;
            }
          }
          dragLine.current = latestLine;
        }

        const handleLine = latestLine ?? ruler;
        updateDragHandles(dragHandle.current?.handle || "", handleLine, dragHandle);

        const alignmentPosition = (() => {
          if (!latestLine) {
            return { x: newX, y: newY };
          }
          if (dragHandle.current.handle === "start") {
            return { x: latestLine.start.x, y: latestLine.start.y };
          }
          if (dragHandle.current.handle === "end") {
            return { x: latestLine.end.x, y: latestLine.end.y };
          }
          return { x: newX, y: newY };
        })();

        updateAlignmentLines(
          ruler.id,
          alignmentPosition.x,
          alignmentPosition.y,
          dragHandle.current.handle,
        );

        const isMiddleHandle = dragHandle.current.handle === "middleV" || dragHandle.current.handle === "middleH";
        if (!isMiddleHandle && !delta.equals(new THREE.Vector2(0, 0))) {
          updateWallConnections(
            ruler.id,
            dragHandle.current?.handle || null,
            [ruler.id],
            floorplannerStore
          );
        }
      }
    }),
    [editorStore.rulerDragging, projectToWorld, updateDragHandles, camera.zoom],
  );

  const handlePointerUp = useCallback(() => {
    const draggedRulerId = dragLine.current?.id;
    aboutToModify.current = false;
    if (dragLine.current) {
      floorplannerStore.recordRulerChange(dragLine.current.id, 'modify');
      floorplannerStore.pushToUndoStack();
    }
    dragLine.current = undefined;
    dragOffset.current = null;
    dragHandle.current.handle = "";
    editorStore.setRulerDragging(null);
    // Change cursor back to auto when dragging is done
    document.body.classList.remove("cursor-vector")
    gl.domElement.removeEventListener("pointermove", handlePointerMove);
    gl.domElement.removeEventListener("pointerup", handlePointerUp);
    floorplannerStore.setBlockDirty(false);
    if (draggedRulerId) floorplannerStore.setDirty();
    editorStore.setWhichEndToUpdate(undefined);
  }, [gl.domElement, handlePointerMove]);


  // Define the callback functions with appropriate types
  const onLinePointerDown = useCallback((event: ThreeEvent<PointerEvent>, id: string) => {
    if (editorStore.rulerDragging || editorStore.wallConstructionMode || editorStore.lineConstructionMode || editorStore.rulerConstructionMode || editorStore.areaConstructionMode || editorStore.areaFillConstructionMode || editorStore.areaBleedFillMode) return;
    // View-only: select to inspect measurements, but don't start a drag.
    if (floorplannerStore.lockedByOther) {
      event.stopPropagation();
      const lockedRuler = floorplannerStore.rulerLinesMap.get(id);
      if (!lockedRuler) return;
      if (!editorStore.isShiftPressed) {
        editorStore.clearSelections();
      }
      editorStore.addSelection(lockedRuler);
      floorplannerStore.selectRuler(lockedRuler.id);
      return;
    }
    event.stopPropagation();
    if (editorStore.selections.length > 1) {
      startDraggingGroupSelection(event);
    }
    // Change cursor to grabbing when dragging a wall
    document.body.classList.add("cursor-vector")
    const ruler = floorplannerStore.rulerLinesMap.get(id);
    if (!ruler) return;
    aboutToModify.current = true;
    dragLine.current = ruler;
    updateDragHandles(dragHandle.current?.handle || "wall", ruler, dragHandle);
    const [worldX, worldY] = projectToWorld(event.clientX, event.clientY, gl, camera);
    const startOffsetX = worldX - ruler.start.x;
    const startOffsetY = worldY - ruler.start.y;
    const endOffsetX = worldX - ruler.end.x;
    const endOffsetY = worldY - ruler.end.y;
    dragOffset.current = [startOffsetX, startOffsetY, endOffsetX, endOffsetY];
    if (!editorStore.isShiftPressed && editorStore.selections.find((s) => s.id === ruler.id) === undefined) {
      editorStore.clearSelections();
    }
    editorStore.addSelection(ruler);
    floorplannerStore.selectRuler(ruler.id);

    floorplannerStore.setBlockDirty(true);
    gl.domElement.addEventListener("pointermove", handlePointerMove);
    gl.domElement.addEventListener("pointerup", handlePointerUp);
    floorplannerStore.setBlockDirty(true);

  }, []);

  const onLinePointerEnter = useCallback((event: ThreeEvent<PointerEvent>, id: string) => {
    if (editorStore.rulerDragging || editorStore.wallConstructionMode || editorStore.lineConstructionMode || editorStore.rulerConstructionMode || editorStore.areaConstructionMode || editorStore.areaFillConstructionMode || editorStore.areaBleedFillMode || renderStore.isDragging) return;
    event.stopPropagation();
    // Change cursor to grab when hovering over the ruler
    if (document.body.style.cursor !== "grabbing" && document.body.style.cursor !== "move") document.body.classList.add("cursor-vector")
    setHoveredLine(id);
  }, []);

  const onLinePointerLeave = useCallback((event: ThreeEvent<PointerEvent>, id: string) => {
    if (editorStore.rulerDragging || editorStore.wallConstructionMode || editorStore.lineConstructionMode || editorStore.rulerConstructionMode || editorStore.areaConstructionMode || editorStore.areaFillConstructionMode || editorStore.areaBleedFillMode || renderStore.isDragging) return;
    // Reset cursor when leaving the ruler
    if (!editorStore.rulerDragging) document.body.classList.remove("cursor-vector")
    setHoveredLine(null);
  }, []);

  const handleKeyPress = useCallback((event: React.KeyboardEvent<HTMLInputElement>, ruler: RulerLineType) => {
    // Implementation for key press handling
    if (event.key === "Enter") {
      // Implementation for handling Enter key press
    }
  }, []);

  const handleLengthClick = useCallback((lineId: string) => {
    // Implementation for handling length click
  }, []);

  const onLineSelectionPointerDown = useCallback(
    (
      event: ThreeEvent<PointerEvent>,
      ruler: RulerLineType,
      handle: "start" | "end" | "middleV" | "middleH" | "control",
    ) => {
      event.stopPropagation();
      aboutToModify.current = true;
      dragLine.current = ruler;
      updateDragHandles(handle, ruler, dragHandle);
      const [worldX, worldY] = projectToWorld(event.clientX, event.clientY, gl, camera);
      let offsetX, offsetY;

      if (handle === "middleV" || handle === "middleH" || handle === "control") {
        const midpointX = (ruler.start.x + ruler.end.x) / 2;
        const midpointY = (ruler.start.y + ruler.end.y) / 2;
        offsetX = worldX - midpointX;
        offsetY = worldY - midpointY;
      } else {
        offsetX = worldX - (handle === "start" ? ruler.start.x : ruler.end.x);
        offsetY = worldY - (handle === "start" ? ruler.start.y : ruler.end.y);
      }
      if (handle === "start" || handle === "end") {
        const isWallEdgeConnected = ruler.centerConnections?.find(
          (c) => (c.targetPosition === WallConnectionStart || c.targetPosition === WallConnectionEnd) &&
            c.sourcePosition === (handle === 'start' ? WallConnectionStart : WallConnectionEnd),
        );
        setDragEndFreeToConnect(!isWallEdgeConnected);
      }

      dragOffset.current = [offsetX, offsetY, offsetX, offsetY];
      if (!editorStore.isShiftPressed && editorStore.selections.find((s) => s.id === ruler.id) === undefined) {
        editorStore.clearSelections();
      }
      editorStore.addSelection(ruler);
      floorplannerStore.selectRuler(ruler.id);
      gl.domElement.addEventListener("pointermove", handlePointerMove);
      gl.domElement.addEventListener("pointerup", handlePointerUp);
      floorplannerStore.setBlockDirty(true);
    },
    [
      floorplannerStore.rulerLinesMap,
      updateDragHandles,
      projectToWorld,
      gl.domElement,
      handlePointerMove,
      handlePointerUp,
      camera.zoom
    ],
  );

  return (
    <group>
      {floorplannerStore.rulerLinesMap.size > 0 &&
        Array.from(floorplannerStore.rulerLinesMap.values()).map((ruler) => {
          const isTranslating = renderStore.isObjectTranslating(ruler.id);
          const offset = isTranslating ? renderStore.groupTranslateOffset : { x: 0, y: 0 };

          // Calculate rotation offset for optimized group rotation
          const isRotating = renderStore.isObjectRotating(ruler.id);
          const rotationData = isRotating
            ? { angle: renderStore.groupRotationAngle, center: renderStore.groupRotationCenter }
            : null;

          // Calculate final position with both translate and rotation offsets
          let finalPosition = [offset.x, offset.y, 0] as [number, number, number];
          let groupRotation = [0, 0, 0] as [number, number, number];
          if (rotationData) {
            const { angle, center } = rotationData;
            const cos = Math.cos(angle);
            const sin = Math.sin(angle);
            const rotationCompX = center.x * (1 - cos) + center.y * sin;
            const rotationCompY = center.y * (1 - cos) - center.x * sin;
            finalPosition = [offset.x + rotationCompX, offset.y + rotationCompY, 0];
            groupRotation = [0, 0, angle];
          }

          return (
            <group
              key={`group-ruler-${ruler.id}`}
              position={finalPosition}
              rotation={groupRotation}
            >
              <RulerLine
                key={`ruler-${ruler.id}`}
                ruler={ruler}
                color={ruler.selected
                  ? CANVAS_ACCENT_COLOR
                  : hoveredLine === ruler.id
                    ? (ruler.hoverColor?.toString() ?? CANVAS_HOVER_COLOR)
                    : ruler.lineColor
                      ? ruler.lineColor.toString()
                      : "black"}
                onLinePointerDown={onLinePointerDown}
                onLinePointerEnter={onLinePointerEnter}
                onLinePointerLeave={onLinePointerLeave}
                handleKeyPress={handleKeyPress}
                handleLengthClick={handleLengthClick}
              />
              {(selectionHandlesVisible && ruler.selected && !editorStore.wallConstructionMode && !editorStore.lineConstructionMode && !editorStore.rulerConstructionMode && !editorStore.areaConstructionMode && !editorStore.areaFillConstructionMode && !editorStore.areaBleedFillMode) && (
                <group>
                  {(editorStore.rulerDragging !== ruler.id || dragHandle.current?.handle === "start") && (
                    <group
                      position={new THREE.Vector3(ruler.start.x, ruler.start.y, 0.02)}
                      onPointerDown={(event) => onLineSelectionPointerDown(event, ruler, "start")}
                      onPointerEnter={(event) => {
                        event.stopPropagation();
                        document.body.style.cursor = "move";
                        setHoveredHandle("start")
                      }}
                      onPointerLeave={(event) => {
                        event.stopPropagation();
                        if (editorStore.rulerDragging !== ruler.id) document.body.style.cursor = "auto";
                        setHoveredHandle(null)
                      }}
                    >
                      <mesh>
                        <planeGeometry args={[(handleWidth * 4) / editorStore.zoomLevelDivisor(), (handleWidth * 4) / editorStore.zoomLevelDivisor()]} />
                        <meshBasicMaterial
                          //color="white"
                          transparent
                          opacity={0.0}
                        />
                      </mesh>
                      <mesh raycast={noopRaycast}>
                        <torusGeometry args={[handleSize / editorStore.zoomLevelDivisor() / 1.8, 0.003, 16, 100]} />
                        <meshBasicMaterial color={CANVAS_ACCENT_COLOR} />
                      </mesh>
                      <mesh
                        position={[0, 0, 0]}
                        rotation={[Math.PI / 2, 0, 0]}
                        raycast={noopRaycast}
                      >
                        <cylinderGeometry args={[handleSize / editorStore.zoomLevelDivisor() / 1.8, 0, 0, 32]} />
                        <meshBasicMaterial color={canvasHandleFillColor(dragHandle.current?.handle === "start", isHovered("start"))} />
                      </mesh>
                    </group>
                  )}
                  {(editorStore.rulerDragging !== ruler.id || dragHandle.current?.handle === "end") && (
                    <group
                      position={new THREE.Vector3(ruler.end.x, ruler.end.y, 0.02)}
                      onPointerDown={(event) => onLineSelectionPointerDown(event, ruler, "end")}
                      onPointerEnter={(event) => {
                        event.stopPropagation();
                        document.body.style.cursor = "move";
                        setHoveredHandle("end")
                      }}
                      onPointerLeave={(event) => {
                        event.stopPropagation();
                        if (editorStore.rulerDragging !== ruler.id) document.body.style.cursor = "auto";
                        setHoveredHandle(null)
                      }}
                    >
                      <mesh>
                        <planeGeometry args={[(handleWidth * 4) / editorStore.zoomLevelDivisor(), (handleWidth * 4) / editorStore.zoomLevelDivisor()]} />
                        <meshBasicMaterial
                          //color="white"
                          transparent
                          opacity={0.0}
                        />
                      </mesh>
                      <mesh raycast={noopRaycast}>
                        <torusGeometry args={[handleSize / editorStore.zoomLevelDivisor() / 1.8, 0.003, 16, 100]} />
                        <meshBasicMaterial color={CANVAS_ACCENT_COLOR} />
                      </mesh>
                      <mesh
                        position={[0, 0, 0]}
                        rotation={[Math.PI / 2, 0, 0]}
                        raycast={noopRaycast}
                      >
                        <cylinderGeometry args={[handleSize / editorStore.zoomLevelDivisor() / 1.8, 0, 0, 32]} />
                        <meshBasicMaterial color={canvasHandleFillColor(dragHandle.current?.handle === "end", isHovered("end"))} />
                      </mesh>
                    </group>
                  )}
                </group>
              )}
            </group>
          );
        })
      }
    </group>
  );
});

export default RulerLines;
